import { ExternalLink } from "lucide-mui";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { keys } from "../../../api/queryKeys.ts";
import { api } from "../../../api/client.ts";
import type { CompanyPressRelease } from "../../../api/client.ts";
import { DetailGridItem } from "../../../components/DetailRow.tsx";
import FormattedDate from "../../../components/FormattedDate.tsx";
import Panel from "../../../components/Panel.tsx";
import ExpandableRow from "../../../components/table/ExpandableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import { humanize } from "../../../utils/format.ts";

// Distributors in `integrations/press_releases`.
const SOURCE_LABELS: Record<string, string> = {
  newsweb: "Newsweb",
  mfn: "MFN",
  nasdaq: "Nasdaq Nordic",
  cision: "Cision",
  ntb: "NTB",
};

function PressReleaseRow({ release }: { release: CompanyPressRelease }) {
  return (
    <ExpandableRow
      colSpan={5}
      cells={
        <>
          <TableCell>
            {release.url ? (
              <Link
                href={release.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {release.title} <ExternalLink fontSize="inherit" />
              </Link>
            ) : (
              release.title
            )}
          </TableCell>
          <TableCell>{SOURCE_LABELS[release.source] ?? release.source}</TableCell>
          <TableCell>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
              {release.is_regulatory && <Chip size="small" color="primary" label="Regulatory" />}
              {release.categories.map((c) => (
                <Chip key={c.key} size="small" variant="outlined" label={c.label} />
              ))}
            </Stack>
          </TableCell>
          <TableCell sx={{ whiteSpace: "nowrap" }}>
            <FormattedDate value={release.published_at} />
          </TableCell>
        </>
      }
    >
      {release.snippet && (
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          {release.snippet}
          {release.snippet.length >= 600 && "…"}
        </Typography>
      )}
      <Grid container spacing={2}>
        <DetailGridItem label="Issuer" value={release.issuer_name} />
        <DetailGridItem label="Language" value={release.language} />
        <DetailGridItem
          label="Match method"
          value={
            release.match_method &&
            (release.match_method === "lei" ? "LEI" : humanize(release.match_method))
          }
        />
      </Grid>
    </ExpandableRow>
  );
}

interface PressReleasesSectionProps {
  companyId: string;
}

// Press releases the company published, linked by LEI, domain or name.
export default function PressReleasesSection({ companyId }: PressReleasesSectionProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data, isError } = useQuery({
    queryKey: keys.masterCompany.pressReleases(companyId, page * rowsPerPage, rowsPerPage),
    queryFn: () =>
      api.getCompanyPressReleases(companyId, { limit: rowsPerPage, offset: page * rowsPerPage }),
    placeholderData: keepPreviousData,
    retry: false,
  });

  if (isError || !data || data.total === 0) return null;

  return (
    <Panel title="Press releases" count={data.total} disablePadding sx={{ mb: 3 }}>
      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Title</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Published</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.items.map((release) => (
            <PressReleaseRow key={release.id} release={release} />
          ))}
        </TableBody>
      </ScrollTable>
      {data.total > 25 && (
        <TablePagination
          component="div"
          count={data.total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
          sx={{ borderTop: 1, borderColor: "divider" }}
        />
      )}
    </Panel>
  );
}
